import api from '@api/apiServices';
import DialogTextField from '@Components/General/dialog-content/text-field';
import { ColumnType, DynamicTable } from '@Components/General/DynamicTable';
import { StatusField } from '@Components/General/DynamicTable/Cells';
import CopyField from '@Components/General/DynamicTable/Cells/CopyField';
import NoDataComponent from '@Components/General/Feedback/no-data-component';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import TableSkeleton from '@Components/General/Skeletons/table';
import useDialog from '@hooks/useDialog';
import {
    CloseRounded,
    DeleteOutlineRounded,
    EditRounded,
    KeyboardArrowDownRounded,
    KeyboardArrowUpRounded,
    PublishedWithChangesRounded,
    VisibilityRounded,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
    Alert,
    Box,
    Button,
    Collapse,
    IconButton,
    InputAdornment,
    TableCell,
    TableRow,
    TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { closeOptions } from '@utils/utils';
import { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

const statusColorMap = {
    Pending: 'warning',
    Failed: 'error',
    Success: 'success',
};

const domainStatuses = {
    Pending: {
        color: 'warning',
        alert: 'It could take up to 24 hours to verify your domain, try again later.',
    },
    Failed: {
        color: 'error',
        alert: 'We could not verify your domain, delete and try again.',
    },
    Success: {
        color: 'success',
        alert: 'Your domain has been verified successfully.',
    },
};

const emailDefaultValues = {
    email: '',
    name: '',
    reply_to: '',
};

const EditEmail = ({ domain, email = emailDefaultValues, onClose }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const isNew = !email?.id;
    const { mutate, isPending } = useMutation({
        mutationKey: isNew ? ['addDomainEmail'] : ['updateDomainEmail'],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['emailDomains'] });
            onClose();
        },
    });
    const { register, getValues } = useForm({
        defaultValues: { ...email, domain: domain.id },
    });

    return (
        <TableRow>
            <TableCell padding="checkbox">
                <TextField
                    fullWidth
                    size="small"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                @{domain.domain}
                            </InputAdornment>
                        ),
                        sx: { minWidth: 200 },
                    }}
                    placeholder={t('Email')}
                    {...register('email')}
                />
            </TableCell>
            <TableCell padding="checkbox">
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('Sender Name')}
                    {...register('name')}
                />
            </TableCell>
            <TableCell padding="checkbox">
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('Reply To Email')}
                    {...register('reply_to')}
                />
            </TableCell>
            <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton size="medium" onClick={onClose}>
                    <CloseRounded />
                </IconButton>
                <LoadingButton
                    variant="filled"
                    size="medium"
                    loading={isPending}
                    onClick={() =>
                        mutate(isNew ? getValues() : [email.id, getValues()])
                    }
                >
                    {t('Save')}
                </LoadingButton>
            </TableCell>
        </TableRow>
    );
};

const DomainEmails = ({ domain }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { domain: domainName, domain_emails: emails } = domain;
    const dialog = useDialog();
    const [edit, setEdit] = useState(null);

    const deleteEmailMutation = useMutation({
        mutationKey: ['deleteDomainEmail'],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['emailDomains'] });
            dialog.close();
            closeOptions();
        },
    });

    const getQuickOptions = (email): QuickOption[] => [
        {
            label: t('Delete'),
            onClick: () =>
                dialog.open({
                    title: t('Delete Email'),
                    content: (
                        <Trans
                            t={t}
                            values={{ email: `${email.email}@${domainName}` }}
                        >
                            prompts:content.deleteSendingEmail
                        </Trans>
                    ),
                    confirmLabel: t('Delete'),
                    confirmButtonProps: { color: 'error' },
                    confirmMutation: deleteEmailMutation,
                    onConfirm: () => email.id,
                }),
            Icon: DeleteOutlineRounded,
            sx: { color: 'error.main' },
        },
        {
            label: t('Edit'),
            onClick: () => setEdit(email.id),
            Icon: EditRounded,
        },
    ];

    const columns: ColumnType[] = [
        {
            name: t('Email'),
            field: 'email',
            renderField: ({ email }) => `${email}@${domainName}`,
            headCellProps: { width: 160 },
        },
        {
            name: t('Sender Name'),
            field: 'name',
            headCellProps: { width: 160 },
        },
        {
            name: t('Reply To Email'),
            field: 'reply_to',
            headCellProps: { width: 160 },
        },
        {
            name: '',
            field: '',
            renderField: row => (
                <Box display="flex" justifyContent="flex-end">
                    <QuickOptions options={getQuickOptions(row)} />
                </Box>
            ),
            rowCellProps: {
                align: 'right',
                padding: 'checkbox',
            },
            headCellProps: { width: 80 },
        },
    ];

    return (
        <DynamicTable
            columns={columns}
            rows={emails}
            noData={
                <NoDataComponent
                    primary={`${t('No Emails for')} ${domainName}`}
                    primaryProps={{ variant: 'titleLarge' }}
                    action={
                        <Button
                            color="secondary"
                            variant="tonal"
                            onClick={() => setEdit('new')}
                        >
                            {t('Add Email')}
                        </Button>
                    }
                />
            }
            tableFooter={
                <Box display="flex" justifyContent="flex-end">
                    <Button
                        color="secondary"
                        variant="tonal"
                        onClick={() => setEdit('new')}
                    >
                        {t('Add Email')}
                    </Button>
                </Box>
            }
            renderRow={(row, tableRowComponent, index) => (
                <Fragment key={index}>
                    {edit === row.id ? (
                        <EditEmail
                            domain={domain}
                            email={row}
                            onClose={() => setEdit(null)}
                        />
                    ) : (
                        tableRowComponent
                    )}
                </Fragment>
            )}
            extraRows={
                edit === 'new' ? (
                    <EditEmail domain={domain} onClose={() => setEdit(null)} />
                ) : null
            }
        />
    );
};

const DnsRecords = ({ domain }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const {
        mutate: verify,
        data,
        isPending,
    } = useMutation({
        mutationFn: () => api.settings.emails.domains.verify[domain.id].post(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['emailDomains'] });
        },
    });

    const statusData = domainStatuses?.[data?.summarized_status];

    const columns: ColumnType[] = [
        {
            name: t('Status'),
            field: 'status',
            headCellProps: { sx: { width: 100 } },
            Component: StatusField,
            colorMap: statusColorMap,
        },
        {
            name: t('Name'),
            field: 'record.name',
            Component: CopyField,
        },
        {
            name: t('Type'),
            field: 'record.type',
            headCellProps: { sx: { width: 80 } },
        },
        {
            name: t('Value'),
            field: 'record.value',
            Component: CopyField,
        },
    ];

    return (
        <>
            <Box
                justifyContent="center"
                display="flex"
                flexDirection="column"
                my={2}
                gap={2}
            >
                <>
                    {data && (
                        <Box justifyContent="center" display="flex">
                            <Alert
                                severity={statusData.color}
                                sx={{ alignItems: 'center' }}
                            >
                                {statusData.alert}
                            </Alert>
                        </Box>
                    )}
                    <Box justifyContent="center" display="flex">
                        <Alert
                            severity="info"
                            sx={{ alignItems: 'center' }}
                            action={
                                <LoadingButton
                                    size="medium"
                                    variant="filled"
                                    startIcon={<PublishedWithChangesRounded />}
                                    loading={isPending}
                                    onClick={verify}
                                >
                                    Verify
                                </LoadingButton>
                            }
                        >
                            Add these DNS records to your domain provider to
                            verify your
                        </Alert>
                    </Box>
                </>
            </Box>
            <DynamicTable
                columns={columns}
                rows={domain.dns_records}
                tableProps={{ sx: { tableLayout: 'fixed' } }}
            />
        </>
    );
};

const useDnsRecordsDialog = () => {
    const dialog = useDialog();
    const { t } = useTranslation();

    return domain =>
        dialog.open({
            title: t('prompts:title.dnsRecords', { domain: domain.domain }),
            dialogProps: {
                maxWidth: 'md',
                PaperProps: { sx: { bgcolor: 'surface.main' } },
            },
            content: <DnsRecords domain={domain} />,
            disableActions: true,
        });
};

const ExpandField = ({ open }) => {
    return (
        <IconButton aria-label="expand row" size="small">
            {open ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
        </IconButton>
    );
};

const ActionField = ({ row }) => {
    const { t } = useTranslation();
    const dialog = useDialog();
    const showDnsRecords = useDnsRecordsDialog();
    const queryClient = useQueryClient();

    const deleteDomainMutation = useMutation({
        mutationFn: () => api.settings.emails.domains.delete(row.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['emailDomains'] });
            dialog.close();
            closeOptions();
        },
    });

    const quickOptions: QuickOption[] = [
        {
            label: t('Delete'),
            onClick: () =>
                dialog.open({
                    title: t('Delete Domain'),
                    content: (
                        <Trans t={t} values={{ domain: row.domain }}>
                            prompts:content.deleteDomain
                        </Trans>
                    ),
                    confirmLabel: t('Delete'),
                    confirmButtonProps: { color: 'error' },
                    confirmMutation: deleteDomainMutation,
                }),
            Icon: DeleteOutlineRounded,
            sx: { color: 'error.main' },
        },
    ];

    return (
        <Box
            display="flex"
            justifyContent="flex-end"
            onClick={e => e.stopPropagation()}
        >
            <IconButton
                aria-label="expand row"
                onClick={() => showDnsRecords(row)}
            >
                <VisibilityRounded />
            </IconButton>
            <QuickOptions options={quickOptions} />
        </Box>
    );
};

const EmailDomains = () => {
    const { t } = useTranslation();
    const dialog = useDialog();
    const showDnsRecords = useDnsRecordsDialog();

    const [expandedRow, setExpandedRow] = useState(null);

    const { control } = useForm({});

    const {
        data: domains,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ['emailDomains'],
        queryFn: () => api.settings.emails.domains.get(),
        initialData: [],
    });

    const addDomainMutation = useMutation({
        mutationFn: data => api.settings.emails.domains.post(data),
        onSuccess: res => {
            refetch();
            dialog.close();
            showDnsRecords(res);
        },
    });

    const columns: ColumnType[] = [
        {
            name: '',
            field: '',
            renderField: ({ id }) => <ExpandField open={expandedRow === id} />,
            headCellProps: { width: 20 },
        },
        {
            name: t('Domain'),
            field: 'domain',
        },
        {
            name: t('Status'),
            field: 'summarized_status',
            headCellProps: { sx: { width: 100 } },
            Component: StatusField,
            colorMap: statusColorMap,
        },
        {
            name: '',
            field: '',
            Component: ActionField,
            rowCellProps: { align: 'right', padding: 'checkbox' },
        },
    ];

    const handleAddDomainClick = () => {
        dialog.open({
            title: t('Add Domain'),
            confirmLabel: t('Add'),
            content: (
                <DialogTextField
                    name="domain"
                    label={t('Domain')}
                    control={control}
                />
            ),
            onConfirm: () => control._formValues,
            confirmMutation: addDomainMutation,
        });
    };

    if (isLoading) return <TableSkeleton columns={2} />;

    if (!domains.length)
        return (
            <NoDataComponent
                primary={t('prompts:noData.emailDomains.primary')}
                secondary={t('prompts:noData.emailDomains.secondary')}
                action={
                    <Button
                        color="secondary"
                        variant="tonal"
                        onClick={handleAddDomainClick}
                    >
                        {t('Add Domain')}
                    </Button>
                }
            />
        );

    return (
        <DynamicTable
            columns={columns}
            rows={domains}
            rowProps={row => ({
                onClick: () =>
                    setExpandedRow(prev => (prev === row.id ? null : row.id)),
                sx: { '& > td': { borderBottom: 'unset' } },
            })}
            renderRow={(row, tableRowComponent, index) => (
                <Fragment key={index}>
                    {tableRowComponent}
                    <TableRow
                        sx={
                            row.id === domains.at(-1).id
                                ? { '& > *': { borderBottom: 'unset' } }
                                : {}
                        }
                    >
                        <TableCell
                            style={{ paddingBottom: 0, paddingTop: 0 }}
                            colSpan={6}
                        >
                            <Collapse
                                in={expandedRow === row.id}
                                timeout="auto"
                                unmountOnExit
                            >
                                <DomainEmails domain={row} />
                            </Collapse>
                        </TableCell>
                    </TableRow>
                </Fragment>
            )}
            tableFooter={
                <Box display="flex" justifyContent="flex-end">
                    <Button
                        color="secondary"
                        variant="tonal"
                        onClick={handleAddDomainClick}
                    >
                        {t('Add Domain')}
                    </Button>
                </Box>
            }
        />
    );
};

export default EmailDomains;
